import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Grid,
  Alert,
  Divider,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  Calculate as CalculateIcon,
} from "@mui/icons-material";
import { RootState } from "../../store/reducers";
import {
  getBrokerageAnniversaries,
  deleteBrokerageAnniversary,
} from "../../store/modules/financialSettings/financialSettingsActions";
import { recalculateFinancialSettings } from "../../store/modules/common/commonActions";
import { clearFinancialSettingsErrors } from "../../store/modules/financialSettings/financialSettingsStateActions";
import BrokerageAnniversaryForm from "./BrokerageAnniversaryForm";
import RecalculateConfirmationDialog from "../Dialogs/RecalculateConfirmationDialog";
import { IBrokerageAnniversaryModel } from "../../types/types";
import { formatCurrency, formatDate } from "../../utils/common";
import { AppDispatch } from "../../store/store";
import { useQuery } from "@tanstack/react-query";

const FinancialSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    brokerageAnniversaries,
    createBrokerageAnniversary: createState,
    updateBrokerageAnniversary: updateState,
    deleteBrokerageAnniversary: deleteState,
  } = useSelector((state: RootState) => state.financialSettings);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAnniversary, setEditingAnniversary] =
    useState<IBrokerageAnniversaryModel | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculateDialogOpen, setRecalculateDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "info" | "error",
  });

  useQuery({
    queryKey: ["getFinancialCategoryDataSummary"],
    queryFn: () => {
      dispatch(getBrokerageAnniversaries());
      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
    enabled: !brokerageAnniversaries.loaded,
  });
  const handleCreate = () => {
    setEditingAnniversary(null);
    setIsFormOpen(true);
  };

  const handleEdit = (anniversary: IBrokerageAnniversaryModel) => {
    setEditingAnniversary(anniversary);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        "Are you sure you want to delete this brokerage anniversary setting?"
      )
    ) {
      dispatch(deleteBrokerageAnniversary(id) as any);
    }
  };

  const handleRecalculateClick = () => {
    setRecalculateDialogOpen(true);
  };

  const handleRecalculateConfirm = async () => {
    setIsRecalculating(true);

    try {
      await recalculateFinancialSettings();
      setSnackbar({
        open: true,
        message:
          "Financial settings for all anniversaries recalculated successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error recalculating financial settings:", error);
      setSnackbar({
        open: true,
        message: "Failed to recalculate financial settings. Please try again.",
        severity: "error",
      });
    } finally {
      setIsRecalculating(false);
      setRecalculateDialogOpen(false);
    }
  };

  const handleRecalculateCancel = () => {
    setRecalculateDialogOpen(false);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingAnniversary(null);
    dispatch(clearFinancialSettingsErrors());
  };

  const handleFormSubmit = (data: any) => {
    setIsFormOpen(false);
    setEditingAnniversary(null);
  };

  const formatPercentage = (percentage: number | undefined) => {
    if (percentage === undefined) return "";
    return `${percentage}%`;
  };

  const loading = brokerageAnniversaries.loading;
  const error =
    brokerageAnniversaries.error ||
    createState.error ||
    updateState.error ||
    deleteState.error;
  const anniversaries = brokerageAnniversaries.data;

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography>Loading ...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" color="primary" fontWeight="600">
          Brokerage Contract Dates
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {anniversaries.length > 0 && (
            <Button
              variant="outlined"
              startIcon={
                isRecalculating ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CalculateIcon />
                )
              }
              onClick={handleRecalculateClick}
              disabled={isRecalculating}
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                whiteSpace: "nowrap",
                px: { xs: 1, sm: 2 },
                "& .MuiButton-startIcon": {
                  mr: { xs: 0.5, sm: 1 },
                },
              }}
            >
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                Recalculate All
              </Box>
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                Recalculate
              </Box>
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              whiteSpace: "nowrap",
              px: { xs: 1, sm: 2 },
              "& .MuiButton-startIcon": {
                mr: { xs: 0.5, sm: 1 },
              },
            }}
          >
            <Box sx={{ display: { xs: "block", sm: "block" } }}>Add</Box>
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Press the "Recalculate All" button after adding new transactions to
          update financial settings for all brokerage contract dates.
        </Typography>
      </Alert>

      {anniversaries.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center", backgroundColor: "#f9f9f9" }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No brokerage contract dates added yet.
          </Typography>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              whiteSpace: "nowrap",
              px: { xs: 1, sm: 2 },
            }}
          >
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              Add Your First Anniversary Date
            </Box>
            <Box sx={{ display: { xs: "block", sm: "none" } }}>
              Add Anniversary
            </Box>
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {anniversaries.map((anniversary: IBrokerageAnniversaryModel) => (
            <Grid item xs={12} md={6} key={anniversary.brokerageAnniversaryId}>
              <Card
                sx={{
                  height: "100%",
                  borderLeft: "4px solid",
                  borderLeftColor: "primary.main",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight="600" gutterBottom>
                        {anniversary.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: "text.secondary",
                        }}
                      >
                        <CalendarIcon sx={{ fontSize: 18, mr: 1 }} />
                        <Typography variant="body2">
                          {formatDate(anniversary.anniversaryDate)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(anniversary)}
                        sx={{ mr: 1 }}
                        title="Edit Anniversary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDelete(anniversary.brokerageAnniversaryId!)
                        }
                        color="error"
                        title="Delete Anniversary"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Commission %
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {formatPercentage(anniversary.commissionPercentage)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Flat Commission
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {formatCurrency(anniversary.flatCommissionFee)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Brokerage Split
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {formatPercentage(anniversary.brokerageSplit)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Split Cap
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {anniversary.splitCap != null
                          ? formatCurrency(anniversary.splitCap)
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Additional % Brokerage Charge
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {formatPercentage(
                          anniversary.additionalChargePercentage
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Cap of Additional % Brokerage Charge
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {anniversary.additionalChargeCap != null
                          ? formatCurrency(anniversary.additionalChargeCap)
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Referral Out Fee Received
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {formatPercentage(anniversary.referralOutFeeReceived)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Agent Referral Fee Paid
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {formatPercentage(anniversary.referralFeePaid)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Rental Commission Percentage
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {formatPercentage(
                          anniversary.rentalCommissionPercentage
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Flat Brokerage Transactional Fees
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {formatCurrency(anniversary.flatTransactionalFees)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <BrokerageAnniversaryForm
        open={isFormOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        initialData={editingAnniversary}
        loading={createState.pending || updateState.pending}
      />

      <RecalculateConfirmationDialog
        open={recalculateDialogOpen}
        onClose={handleRecalculateCancel}
        onConfirm={handleRecalculateConfirm}
        anniversaryName="all anniversaries"
        loading={isRecalculating}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
          elevation={6}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialSettings;
